import type { ReactNode } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { cn } from '@/lib/utils';
import { AlertTriangle, Info } from 'lucide-react';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  loading?: boolean;
  /** Optional extra field(s) between the description and the buttons — e.g. a
   *  reason box on a confirmation that benefits from one. */
  children?: ReactNode;
}

/**
 * Canonical confirmation dialog for the app. ConfirmModal wraps this component,
 * so this is the single implementation to maintain.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  onConfirm,
  loading = false,
  children,
}: ConfirmDialogProps) {
  const handleConfirm = () => {
    onConfirm();
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const icon = {
    default: null,
    danger: { node: <AlertTriangle className="h-6 w-6 text-danger-600 dark:text-danger-400" />, bg: 'bg-danger-50 dark:bg-danger-900/30' },
    warning: { node: <AlertTriangle className="h-6 w-6 text-warning-600 dark:text-warning-400" />, bg: 'bg-warning-50 dark:bg-warning-900/30' },
    info: { node: <Info className="h-6 w-6 text-primary-600 dark:text-primary-400" />, bg: 'bg-primary-50 dark:bg-primary-900/30' },
  }[variant];

  return (
    <Modal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={title}
      size="sm"
      showCloseButton={false}
    >
      <div className="space-y-4">
        {icon && (
          <div className="flex justify-center">
            <div className={cn('flex h-12 w-12 items-center justify-center rounded-xl', icon.bg)}>{icon.node}</div>
          </div>
        )}
        <p className="text-sm text-gray-600 dark:text-gray-400 text-center text-balance">{description}</p>
        {children}
        <div className="flex gap-3 justify-end pt-2">
          <Button variant="secondary" onClick={handleCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={handleConfirm}
            isLoading={loading}
            disabled={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
